package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.components.marketing.AuthPageScaffold
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.Space
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * Where a confirmation email lands.
 *
 * Confirms on load rather than asking for a click. The person already clicked — in their inbox — and
 * making them click again to prove it is the kind of ceremony that reads as distrust.
 */
@Page("/verify-email")
@Composable
fun VerifyEmailPage() {
    val ctx = rememberPageContext()
    val token = ctx.route.queryParams["token"].orEmpty()
    var state by remember { mutableStateOf<VerifyState>(VerifyState.Working) }

    LaunchedEffect(Unit) { document.title = tr("verify_email.confirm_your_email_lingvo", "Confirm your email — Lingvo") }

    LaunchedEffect(token) {
        if (token.isBlank()) {
            state = VerifyState.Failed(
                tr(
                    "verify_email.this_link_is_missing_its_token",
                    "This link is missing its token. Open the link from your email again.",
                ),
            )
            return@LaunchedEffect
        }
        state = try {
            ApiClient.verifyEmail(token)
            VerifyState.Confirmed
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: ApiException) {
            VerifyState.Failed(failure.message.orEmpty())
        } catch (failure: Throwable) {
            VerifyState.Failed(
                tr(
                    "verify_email.couldnt_reach_lingvo_check_your_connection",
                    "Couldn't reach Lingvo. Check your connection and open the link again.",
                ),
            )
        }
    }

    AuthPageScaffold(
        title = when (state) {
            VerifyState.Working -> t("verify_email.confirming_your_email", "Confirming your email")
            VerifyState.Confirmed -> t("verify_email.email_confirmed", "Email confirmed")
            is VerifyState.Failed -> t("verify_email.that_link_didnt_work", "That link didn't work")
        },
        subtitle = when (state) {
            VerifyState.Working -> t("verify_email.one_moment", "One moment.")
            VerifyState.Confirmed -> t(
                "verify_email.your_account_is_complete_nothing_else",
                "Your account is complete. Nothing else to do.",
            )
            // The duration, because "links expire" leaves a reader unable to tell a link that died
            // in ten minutes from one that died last month — and the difference decides whether
            // they hunt for a newer email or ask for a fresh one. Three days is
            // `PasswordResetRoutes.VERIFICATION_LIFETIME`, which is also what the email itself is
            // sent with. `ForgotPasswordPage` states its own two hours the same way.
            is VerifyState.Failed -> t(
                "verify_email.confirmation_links_expire_after_three_days",
                "Confirmation links expire after three days, and each one works once.",
            )
        },
        alternatePrompt = t("verify_email.need_the_app", "Need the app?"),
        alternateAction = t("verify_email.go_to_lingvo", "Go to Lingvo"),
        alternatePath = "/app",
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            when (val current = state) {
                VerifyState.Working -> LSkeleton(Modifier.fillMaxWidth(), label = t("verify_email.confirming_your_email", "Confirming your email"))

                VerifyState.Confirmed -> LButton(
                    label = t("verify_email.open_lingvo", "Open Lingvo"),
                    onClick = { ctx.router.tryRoutingTo("/app") },
                    modifier = Modifier.fillMaxWidth(),
                )

                is VerifyState.Failed -> {
                    // Read before the call rather than inside `ifBlank`, so the lookup is a plain
                    // value in a composable scope instead of a composable call in a lambda.
                    val whenTheServerSaidNothing = t(
                        "verify_email.this_link_has_already_been_used",
                        "This link has already been used or has expired.",
                    )
                    LAlert(
                        title = t("verify_email.couldnt_confirm_this_address", "Couldn't confirm this address"),
                        description = current.message.ifBlank { whenTheServerSaidNothing },
                        modifier = Modifier.fillMaxWidth(),
                        variant = LAlertVariant.WARNING,
                        // This replaced `LSkeleton`, whose `role="status"` had just announced
                        // "Confirming your email". Without a live region here the region simply
                        // disappears and the answer the reader was waiting for is never spoken.
                        live = true,
                    )
                    // Requesting a fresh link needs a session, so send them where they can get one.
                    LButton(
                        label = t("verify_email.sign_in_to_send_a_new", "Sign in to send a new link"),
                        onClick = { ctx.router.tryRoutingTo("/login") },
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }
        }
    }
}

private sealed interface VerifyState {
    data object Working : VerifyState
    data object Confirmed : VerifyState
    data class Failed(val message: String) : VerifyState
}
